import { type RankingItem } from '../types';
import { Trophy, Award, RotateCcw, Download, Table } from 'lucide-react';
import { sortByRank } from '../utils/ranking';
import { exportToExcel, sanitizeSheetName } from '../../../lib/excelService';

interface ResultsViewProps {
  items: RankingItem[];
  onReset: () => void;
}

export function ResultsView({ items, onReset }: ResultsViewProps) {
  const rankedItems = sortByRank(items);

  const handleExport = () => {
    const data = rankedItems.map((item, index) => ({
      rank: index + 1,
      name: item.name,
      score: item.score,
      wins: item.wins,
      losses: item.losses,
      winRate: item.comparisons > 0 
        ? ((item.wins / item.comparisons) * 100).toFixed(1) + '%'
        : '0%',
    }));

    const csv = [
      ['Rank', 'Name', 'Score', 'Wins', 'Losses', 'Win Rate'],
      ...data.map(item => [
        item.rank,
        item.name,
        item.score,
        item.wins,
        item.losses,
        item.winRate,
      ]),
    ]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pairwise-ranking-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportExcel = () => {
    const resultsData = rankedItems.map((item, index) => ({
      rank: index + 1,
      name: item.name,
      score: item.score,
      wins: item.wins,
      losses: item.losses,
      comparisons: item.comparisons,
      win_rate: item.comparisons > 0 
        ? ((item.wins / item.comparisons) * 100).toFixed(1) + '%'
        : '0%',
    }));

    const statsData = [
      { metric: 'Total Items', value: String(rankedItems.length) },
      { metric: 'Total Comparisons', value: String(rankedItems.reduce((sum, i) => sum + i.comparisons, 0) / 2) },
      { metric: 'Top Ranked Item', value: rankedItems[0]?.name || 'N/A' },
      { metric: 'Top Score', value: String(rankedItems[0]?.score || 0) },
      { metric: 'Export Date', value: new Date().toISOString() },
    ];

    exportToExcel({
      filename: `pairwise-ranking-${new Date().toISOString().split('T')[0]}.xlsx`,
      sheets: [
        {
          name: sanitizeSheetName('Rankings'),
          data: resultsData,
          columns: [
            { key: 'rank', header: 'Rank', width: 8 },
            { key: 'name', header: 'Item Name', width: 40 },
            { key: 'score', header: 'ELO Score', width: 12 },
            { key: 'wins', header: 'Wins', width: 10 },
            { key: 'losses', header: 'Losses', width: 10 },
            { key: 'comparisons', header: 'Comparisons', width: 12 },
            { key: 'win_rate', header: 'Win Rate', width: 12 },
          ],
        },
        {
          name: sanitizeSheetName('Statistics'),
          data: statsData,
          columns: [
            { key: 'metric', header: 'Metric', width: 25 },
            { key: 'value', header: 'Value', width: 30 },
          ],
        },
      ],
    });
  };

  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Trophy className="h-6 w-6 text-[var(--mustard)]" />;
    if (rank === 2) return <Award className="h-6 w-6 text-[var(--text-ter)]" />;
    if (rank === 3) return <Award className="h-6 w-6 text-[var(--mustard)]" />;
    return null;
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-[var(--bg-raised)] shadow-sm border border-[var(--rule)] p-6 mb-6">
        <h2 className="text-3xl font-bold text-[var(--text-pri)] mb-2">
          Ranking Results
        </h2>
        <p className="text-[var(--text-sec)] mb-6">
          Your items ranked by preference based on your comparisons.
        </p>

        <div className="space-y-3 mb-6">
          {rankedItems.map((item, index) => {
            const rank = index + 1;
            const winRate = item.comparisons > 0 
              ? (item.wins / item.comparisons) * 100 
              : 0;

            return (
              <div
                key={item.id}
                className="flex items-center gap-4 p-4 bg-[var(--surface)] border border-[var(--rule)] hover:border-[var(--slate)] transition-colors"
              >
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-[var(--bg-raised)] border-2 border-[var(--rule)] font-bold text-lg text-[var(--text-sec)]">
                  {getRankIcon(rank) || rank}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-[var(--text-pri)] text-lg">
                    {item.name}
                  </h3>
                  <div className="flex items-center gap-4 mt-1 text-sm text-[var(--text-sec)]">
                    <span>Score: <strong>{item.score}</strong></span>
                    <span>Wins: {item.wins}</span>
                    <span>Losses: {item.losses}</span>
                    <span>Win Rate: {winRate.toFixed(1)}%</span>
                  </div>
                </div>
                <div className="w-32">
                  <div className="w-full bg-[var(--hover-tint)] rounded-full h-2">
                    <div
                      className="bg-[var(--slate)] h-2 rounded-full transition-all"
                      style={{
                        width: `${((item.score - rankedItems[rankedItems.length - 1].score) / 
                          (rankedItems[0].score - rankedItems[rankedItems.length - 1].score)) * 100}%`,
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleExport}
            className="flex-1 px-4 py-2 bg-[var(--hover-tint)] text-[var(--text-sec)] hover:bg-[var(--hover-tint)] transition-colors flex items-center justify-center gap-2"
          >
            <Download className="h-5 w-5" />
            Export CSV
          </button>
          <button
            onClick={handleExportExcel}
            className="flex-1 px-4 py-2 bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] hover:bg-[var(--green-dim)] transition-colors flex items-center justify-center gap-2"
          >
            <Table className="h-5 w-5" />
            Export Excel
          </button>
          <button
            onClick={onReset}
            className="flex-1 px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] transition-colors flex items-center justify-center gap-2"
          >
            <RotateCcw className="h-5 w-5" />
            Start Over
          </button>
        </div>
      </div>
    </div>
  );
}

